import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/core/services/app.service';
import { FocuslinkService } from 'src/app/core/services/focuslink.service';
import { OOVenueService } from 'src/app/core/services/venue-service';
import { EmployeeRecord } from 'src/app/shared/models/EmployeeRecord.model';
import { AppMessageSeverity } from 'src/app/shared/models/enums/AppMessageSeverity.enum';
import { Job } from 'src/app/shared/models/focusDataModels/Job';
import {
	EmployeeLaborScheduleEntry,
	EmployeeLaborScheduleEntryCost,
	LaborScheduleEntry,
	LaborScheduleItem,
	LaborScheduleStatus,
	ScheduleEntryDay,
} from 'src/app/shared/models/laborScheduling/LaborScheduling.model';
import { LaborSetupItem } from 'src/app/shared/models/laborScheduling/LaborSetup.item';
import { LaborShift } from 'src/app/shared/models/laborScheduling/LaborShift.model';

@Component({
	selector: 'app-employee-schedule-record',
	templateUrl: './employee-schedule-record.component.html',
	styleUrls: ['../../../commandcenter.scss'],
})
export class EmployeeScheduleRecordComponent implements OnInit, OnDestroy {
	accessibleEmployeeJobs: Job[];
	laborSchedule: LaborScheduleItem;

	changesToSave: boolean = false;

	dragDropLaborScheduleEntry: LaborScheduleEntry;

	startDatePrimeNgFormat: Date;
	endDatePrimgeNgFormat: Date;

	employeeList: EmployeeRecord[];

	jobList: Job[];

	laborShiftList: LaborShift[];
	laborSetup: LaborSetupItem;

	maximumShiftsPerEmployeeDisplayed: number = 1;

	published: boolean = false;

	selectedEntry: LaborScheduleEntry;
	selectedLaborShift: LaborShift;

	scheduleDays;

	subscriptions: Subscription = new Subscription();

	constructor(
		public app: AppService,
		public venueService: OOVenueService,
		private focuslink: FocuslinkService,
		private router: Router,
		private activatedRoute: ActivatedRoute
	) {
		this.scheduleDays = Array(7)
			.fill(0)
			.map((x, i) => i);
	}
	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	ngOnInit(): void {
		this.app.loading(true);
		console.clear();
		this.subscriptions.add(
			this.venueService.laborShiftList.subscribe((data) => {
				this.laborShiftList = data;
				this.laborShiftList.sort((a, b) => (a.jobId > b.jobId ? -1 : 1));
				if (this.jobList) {
					this.jobsToLaborShifts();
				}
			})
		);
		this.subscriptions.add(
			this.venueService.jobList.subscribe((data) => {
				this.jobList = data;
				if (this.laborShiftList) {
					this.jobsToLaborShifts();
				}
			})
		);

		this.subscriptions.add(this.venueService.laborSetup.subscribe((data) => (this.laborSetup = data)));

		const externalId = this.activatedRoute.snapshot.paramMap.get('scheduleExternalId');
		if (externalId === 'new-schedule') {
			this.createNewSchedule();
		} else {
			this.getLaborSchedule(this.venueService.subscriptionVenue.value.key, externalId);
		}

		this.employeeList = this.venueService.employeeList.value;
		this.jobList = this.venueService.jobList.value;
		this.laborShiftList = this.venueService.laborShiftList.value.sort((a, b) => (a.jobId > b.jobId ? -1 : 1));
		this.accessibleEmployeeJobs = [...this.jobList];
		this.jobsToLaborShifts();
	}

	addNewRow(event) {
		this.laborSchedule.employeeLaborScheduleEntries.push(
			new EmployeeLaborScheduleEntry({
				employeeId: event.value,
				employee: this.employeeList.find((i) => i.id === event.value),
			})
		);

		this.laborSchedule.employeeLaborScheduleEntries = [...this.laborSchedule.employeeLaborScheduleEntries];

		this.evaluateLaborShiftsByEmployee();
	}

	addLaborScheduleEntryToArray(laborScheduleEntry: LaborScheduleEntry) {
		this.laborSchedule.LaborScheduleEntry.push(laborScheduleEntry);
		this.changesToSave = true;
		this.published = false;

		this.buildScheduleView(this.laborSchedule);
	}

	attachLaborShiftToSchedule(laborShift: LaborShift, employeeId: number, dayIndex: number) {
		let laborScheduleEntry = new LaborScheduleEntry({
			externalId: this.app.uuidv4(),
			employeeId: employeeId,
			employee: this.employeeList.find((i) => i.id === employeeId),
			jobId: laborShift.jobId,
			startDay: dayIndex,
			startDateTime: this.app.moment(this.laborSchedule.startDate).add(dayIndex, 'day').format('MMDDYYYY'),
			endDateTime: this.app.moment(this.laborSchedule.startDate).add(dayIndex, 'day').format('MMDDYYYY'),
			cost: 0,
			shiftColor: laborShift.color,
			shiftNotes: laborShift.note,
		});

		laborScheduleEntry = this.attachLaborEntryTimeFromString(laborShift.startTime, laborShift.endTime, laborScheduleEntry);

		this.validateExistingLaborScheduleEntryConflict(laborScheduleEntry);
	}

	attachExistingEntryToSchedule(existingEntry: LaborScheduleEntry, employeeId: number, dayIndex: number) {
		let laborScheduleEntry = new LaborScheduleEntry({
			externalId: this.app.uuidv4(),
			employeeId: employeeId,
			employee: this.employeeList.find((i) => i.id === employeeId),
			jobId: existingEntry.jobId,
			startDay: dayIndex,
			startDateTime: this.app.moment(this.laborSchedule.startDate).add(dayIndex, 'day').format('MMDDYYYY'),
			endDateTime: this.app.moment(this.laborSchedule.startDate).add(dayIndex, 'day').format('MMDDYYYY'),
			cost: 0,
			shiftColor: existingEntry.shiftColor,
		});

		laborScheduleEntry = this.attachLaborEntryTimeFromString(
			this.app.moment(existingEntry.startDateTime).format('HH:mm'),
			this.app.moment(existingEntry.endDateTime).format('HH:mm'),
			laborScheduleEntry
		);

		this.validateExistingLaborScheduleEntryConflict(laborScheduleEntry);
	}

	attachLaborEntryTimeFromString(startTimeHHmm: string, endTimeHHmm: string, laborScheduleEntry: LaborScheduleEntry): LaborScheduleEntry {
		laborScheduleEntry.startDateTime = this.app.moment(`${laborScheduleEntry.startDateTime} ${startTimeHHmm}`, 'MMDDYYYY HH:mm').format();
		laborScheduleEntry.endDateTime = this.app.moment(`${laborScheduleEntry.endDateTime} ${endTimeHHmm}`, 'MMDDYYYY HH:mm').format();
		laborScheduleEntry.payRate = laborScheduleEntry.employee.jobs.find((i) => i.id === laborScheduleEntry.jobId).payRate;

		const startMoment = this.app.moment(laborScheduleEntry.startDateTime);
		const endMoment = this.app.moment(laborScheduleEntry.endDateTime);

		const durationOfMinutes = endMoment.diff(startMoment, 'minute');

		laborScheduleEntry.cost = (laborScheduleEntry.payRate / 60) * durationOfMinutes;
		laborScheduleEntry.minutes = durationOfMinutes;

		return laborScheduleEntry;
	}

	buildScheduleView(laborSchedule: LaborScheduleItem): LaborScheduleItem {
		this.laborSchedule = new LaborScheduleItem(laborSchedule);
		this.laborSchedule.status === LaborScheduleStatus.PUBLISHED && (this.published = true);
		if (this.laborSchedule.LaborScheduleEntry.length === 0) {
			this.laborSchedule.employeeLaborScheduleEntries = [];
		}
		this.laborSchedule.LaborScheduleEntry.forEach((lse) => {
			lse.startDay = this.app.moment(lse.startDateTime).day();
			if (lse.employeeId) {
				lse.employee = this.employeeList.find((i) => i.id === lse.employeeId);
				lse.job = this.venueService.jobList.value.find((i) => i.id === lse.jobId);
			}

			let employeeEntry = this.laborSchedule.employeeLaborScheduleEntries.find((i) => i.employeeId === lse.employeeId);
			let newEntry: boolean = false;
			if (!employeeEntry) {
				newEntry = true;
				this.laborSchedule.employeeLaborScheduleEntries.push(
					new EmployeeLaborScheduleEntry({
						employeeId: lse.employeeId,
						employee: lse.employee,
					})
				);
			}
		});
		this.startDatePrimeNgFormat = this.app.moment(this.laborSchedule.startDate).toDate();

		this.dateChange(this.laborSchedule.startDate);
		this.app.loading(false);
		return this.laborSchedule;
	}

	back() {
		this.router.navigate(['/cc/employees/schedules']);
	}

	calculateLaborScheduleEntryValues(scheduleEntryDay: ScheduleEntryDay[]): EmployeeLaborScheduleEntryCost {
		let costs = new EmployeeLaborScheduleEntryCost();

		scheduleEntryDay.forEach((event) => {
			costs = this.calculateDailyOvertimeValuesFromLaborRules(event.entries, costs);
			costs.totalMinutes += event.entries?.reduce((acc, obj) => acc + obj.minutes, 0);
		});
		costs = this.calcWeeklyOvertimeFromLaborRules(scheduleEntryDay, costs);

		return costs;
	}

	calculateDailyOvertimeValuesFromLaborRules(entries: LaborScheduleEntry[], costs: EmployeeLaborScheduleEntryCost): EmployeeLaborScheduleEntryCost {
		if (entries.length === 0) {
			return costs;
		}

		let employeeRegularTimeHours: number = 0;
		let hoursForDay: number = 0;
		const employeeId = entries[0].employeeId;

		entries.forEach((entry) => {
			const laborRule = this.laborSetup.jobSpecificRules?.find((i) => i.jobId === entry.jobId) || this.laborSetup;
			entry.hours = this.app.moment.duration(entry.minutes, 'minutes').hours();
			hoursForDay += entry.hours;
			if (
				(laborRule.dailyDoubleOvertime || laborRule.dailyOvertime) &&
				(hoursForDay > laborRule.dailyOvertimeHours || hoursForDay > laborRule.dailyDoubleOvertimeHours)
			) {
				if (hoursForDay > laborRule.dailyOvertimeHours) {
					hoursForDay += entry.hours;
					let hoursWorkedForShift = entry.hours;

					let dailyDoubleHours: number = 0;
					let dailyDoubleCost: number = 0;
					let dailyOvertimeHours: number = 0;
					let dailyOvertimeCost: number = 0;
					let accountedForHours: number = 0;

					if (hoursForDay > laborRule.dailyDoubleOvertimeHours) {
						// did this entry cause us to go into daily double?
						if (hoursForDay - entry.hours < laborRule.dailyDoubleOvertimeHours) {
							dailyDoubleHours = laborRule.dailyDoubleOvertimeHours - entry.hours;
							dailyDoubleCost = dailyDoubleHours * (entry.payRate & laborRule.dailyDoubleOvertimeRate);
						} else {
							//WE WERE ALREADY IN DAILY DOUBLE OVERTIME
							dailyDoubleHours = entry.hours;
							dailyDoubleCost = dailyDoubleHours * (entry.payRate & laborRule.dailyDoubleOvertimeRate);
						}

						hoursWorkedForShift = hoursWorkedForShift - dailyDoubleHours;
						accountedForHours = dailyDoubleHours;
					}

					let hoursRemaining = hoursForDay - accountedForHours;
					if (laborRule.dailyOvertime && hoursRemaining > laborRule.dailyOvertimeHours) {
						dailyOvertimeHours = hoursRemaining - laborRule.dailyOvertimeHours;
						dailyOvertimeCost = dailyOvertimeHours * (entry.payRate * laborRule.dailyOvertimeRate);
						hoursRemaining = hoursForDay - dailyOvertimeHours;
					}

					costs.dailyOvertimeCost += dailyOvertimeCost + dailyDoubleCost;
					costs.dailyOvertimeHours += dailyDoubleHours + dailyOvertimeHours;
					hoursRemaining > 0 && (costs.regularHours += hoursRemaining);
					hoursRemaining > 0 && costs.regularHours * entry.payRate;
				} else {
					employeeRegularTimeHours += entry.hours;
					costs.regularHours += entry.hours;
					costs.regularCost += entry.cost;
				}
			} else {
				costs.regularHours += entry.hours;
				costs.regularCost += entry.cost;
			}
		});

		return costs;
	}

	calcWeeklyOvertimeFromLaborRules(scheduleEntryDayList: ScheduleEntryDay[], costs: EmployeeLaborScheduleEntryCost): EmployeeLaborScheduleEntryCost {
		let regularTimeHours: number = 0;
		let runningWeeklyOvertimeHours: number = 0;
		let runningWeeklyOvertimeCost: number = 0;
		let runningRegularTimeCostOffsetDueToWeeklyOvertime: number = 0;

		let hoursForWeek: number = 0;
		scheduleEntryDayList.forEach((scheduleEntryDay) => {
			scheduleEntryDay.entries?.forEach((entry) => {
				hoursForWeek += entry.hours;
				const hours = this.app.moment.duration(entry.minutes, 'minutes').hours();
				const laborRule = this.laborSetup.jobSpecificRules?.find((i) => i.jobId === entry.jobId) || this.laborSetup;

				if (hoursForWeek > laborRule.weeklyOvertimeHours) {
					const totalHoursThusFar = hoursForWeek;

					const incurredOTHours = totalHoursThusFar - laborRule.weeklyOvertimeHours; //6 hrs

					runningWeeklyOvertimeHours += incurredOTHours;

					runningWeeklyOvertimeCost += incurredOTHours * (entry.payRate * laborRule.weeklyOvertimeRate);

					const incurredRegularTimeHours = hours - incurredOTHours;

					runningRegularTimeCostOffsetDueToWeeklyOvertime += incurredRegularTimeHours * entry.payRate;

					regularTimeHours += incurredRegularTimeHours;
				}
			});
		});

		costs.weeklyOvertimeCost += runningWeeklyOvertimeCost;
		costs.weeklyOvertimeHours += runningWeeklyOvertimeHours;
		costs.regularHours -= runningWeeklyOvertimeHours;
		costs.regularCost -= runningRegularTimeCostOffsetDueToWeeklyOvertime;

		return costs;
	}

	createNewSchedule() {
		this.laborSchedule = new LaborScheduleItem({
			pk: this.venueService.subscriptionVenue.value.key.toString(),
			externalId: this.app.uuidv4(),
			status: LaborScheduleStatus.DRAFT,
			LaborScheduleEntry: [],
		});
		this.laborSchedule = this.buildScheduleView(this.laborSchedule);
		this.app.loading(false);
	}

	dateChange(event) {
		this.laborSchedule.startDate = this.app.moment(event).format();
		this.laborSchedule.endDate = this.app.moment(this.laborSchedule.startDate).add(6, 'day');

		this.laborSchedule.employeeLaborScheduleEntries.forEach((employeeEntry) => {
			employeeEntry.scheduleEntryDays.forEach((scheduleEntry, idx) => {
				scheduleEntry.date = this.app.moment(this.laborSchedule.startDate).add(idx, 'day').format('MMDDYYYY');
				scheduleEntry.entries = this.mapScheduleEntryFromLaborSchedule(employeeEntry.employeeId, scheduleEntry.date);
				if (this.maximumShiftsPerEmployeeDisplayed < scheduleEntry.entries.length) {
					this.maximumShiftsPerEmployeeDisplayed = scheduleEntry.entries.length;
				}
			});
			employeeEntry.cost = this.calculateLaborScheduleEntryValues(employeeEntry.scheduleEntryDays);
		});

		this.evaluateLaborShiftsByEmployee();
	}

	async delete() {
		await this.focuslink.laborScheduleDelate(this.venueService.subscriptionVenue.value.key, this.laborSchedule.externalId);
		this.back();
	}

	deleteDisabled() {
		if (
			!this.laborSchedule.externalId ||
			this.laborSchedule.status === LaborScheduleStatus.SENT ||
			this.laborSchedule.status === LaborScheduleStatus.PUBLISHED
		) {
			return true;
		}
		return false;
	}

	deleteRowPrompt(employeeId: number) {
		this.app.confirmation.confirm({
			message: `Are you sure you want to delete ${
				this.employeeList.find((i) => i.id === employeeId).nickName
			}? \nThis will delete all schedule entries for the employee.`,
			accept: async () => {
				this.deleteRow(employeeId);
			},
			reject: async () => {
				this.app.confirmation.close();
			},
		});
	}

	deleteRow(employeeId: number) {
		const lseForEmployee = this.laborSchedule.LaborScheduleEntry.filter((i) => i.employeeId === employeeId);
		const lseIndexList = lseForEmployee.map((lse, idx) => idx).reverse();
		for (let i = lseIndexList.length - 1; i >= 0; i--) {
			this.laborSchedule.LaborScheduleEntry.splice(i, 1);
		}

		this.laborSchedule.employeeLaborScheduleEntries.splice(
			this.laborSchedule.employeeLaborScheduleEntries.findIndex((i) => i.employeeId === employeeId),
			1
		);
	}

	entryDelete(scheduleEntry: LaborScheduleEntry) {
		this.laborSchedule.LaborScheduleEntry.splice(
			this.laborSchedule.LaborScheduleEntry.findIndex((i) => i.externalId === scheduleEntry.externalId),
			1
		);
		this.buildScheduleView(this.laborSchedule);
		this.changesToSave = true;
		this.published = false;
	}

	entrySave(scheduleEntry: LaborScheduleEntry) {
		const existingIdx = this.laborSchedule.LaborScheduleEntry.findIndex((i) => i.externalId === scheduleEntry.externalId);
		if (existingIdx != -1) {
			this.laborSchedule.LaborScheduleEntry[existingIdx] = scheduleEntry;
		} else {
			this.laborSchedule.LaborScheduleEntry.push(scheduleEntry);
		}

		this.buildScheduleView(this.laborSchedule);
		this.changesToSave = true;
		this.published = false;
	}

	entryDragStart(event, laborScheduleEntry: LaborScheduleEntry) {
		this.dragDropLaborScheduleEntry = laborScheduleEntry;
	}

	entryDragEnd(event, laborScheduleEntry: LaborScheduleEntry) {
		this.dragDropLaborScheduleEntry = undefined;
	}

	entryDestinationDragDrop(event, employeeId: number, dayIndex: number) {}

	evaluateLaborShiftsByEmployee() {
		const employeeIds = this.laborSchedule.employeeLaborScheduleEntries.map((i) => i.employeeId);
		let jobsIdList: number[] = [];
		employeeIds.forEach((ee) => {
			const employee = this.employeeList.find((emp) => emp.id === ee);
			jobsIdList = jobsIdList.concat(employee.jobs.map((i) => i.id));
		});
		jobsIdList = Array.from(new Set(jobsIdList));
		this.laborShiftList.forEach((shift) => {
			if (jobsIdList.includes(shift.jobId)) {
				shift.visible = true;
			} else {
				shift.visible = false;
			}
		});

		this.laborShiftList = [...this.laborShiftList];
	}

	filterEmployeesForJobs(event) {
		const accessibleEmployeeJobIdList = this.accessibleEmployeeJobs.map((i) => i.id);
		this.laborSchedule.employeeLaborScheduleEntries.forEach((eeEntry) => {
			eeEntry.display = eeEntry.employee.jobs.some((job) => accessibleEmployeeJobIdList.includes(job.id));
		});

		this.laborSchedule.employeeLaborScheduleEntries = [...this.laborSchedule.employeeLaborScheduleEntries];
	}

	async getLaborSchedule(venueKey: number, externalId: string): Promise<LaborScheduleItem> {
		const resp = await this.focuslink.laborScheduleGetOne(venueKey, externalId);
		this.laborSchedule = this.buildScheduleView(resp);
		return this.laborSchedule;
	}

	jobsToLaborShifts() {
		this.laborShiftList.forEach((shift) => {
			shift.job = this.jobList.find((i) => i.id === shift.jobId);
			shift.jobName = shift.job.name;
		});
	}

	async publishScheduleRecord() {
		if (this.saveLabel() === 'Publish') {
			await this.saveScheduleRecord(true);
			this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: '', detail: `Schedule published` });
		}
	}

	async saveButtonAction() {
		switch (this.saveLabel()) {
			case 'Publish':
				this.publishScheduleRecord();
				break;
			case 'Save':
				this.saveScheduleRecord();
				break;
		}
	}

	async saveScheduleRecord(publish?: boolean) {
		const saveValue = this.app.clone(this.laborSchedule);

		delete saveValue.employeeLaborScheduleEntries;
		saveValue.LaborScheduleEntry.forEach((lse) => {
			delete lse.employee;
			delete lse.job;
		});
		const resp = await this.focuslink.laborScheduleSave(this.venueService.subscriptionVenue.value.key, saveValue, publish);
		if (!publish) {
			this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: '', detail: `${this.laborSchedule.name} saved` });
		}

		this.changesToSave = false;
	}

	mapScheduleEntryFromLaborSchedule(employeeId: number, startDate: string): LaborScheduleEntry[] {
		return this.laborSchedule.LaborScheduleEntry.filter(
			(lse) => employeeId === lse.employeeId && this.app.moment(lse.startDateTime).format('MMDDYYYY') === startDate
		);
	}

	saveDisabled() {
		if (this.saveLabel() === 'Publish') {
			if (this.published) {
				return true;
			}
			return false;
		}
		if (!this.laborSchedule.startDate || !this.laborSchedule.endDate || !this.changesToSave) {
			return true;
		}

		return false;
	}

	saveLabel() {
		if (this.laborSchedule.externalId && !this.changesToSave) {
			return 'Publish';
		}
		return 'Save';
	}

	selectEmployeeScheduleEvent(scheduleEntry: LaborScheduleEntry, scheduleEntryDay: ScheduleEntryDay, employeeId: number) {
		if (scheduleEntry) {
			this.selectedEntry = scheduleEntry;
			return;
		}
		this.selectedEntry = new LaborScheduleEntry({
			employeeId: employeeId,
			employee: this.employeeList.find((i) => i.id === employeeId),
			startDay: scheduleEntryDay.dayOfWeek,
		});
	}

	shiftDragStart(event, laborShift: LaborShift) {
		this.selectedLaborShift = laborShift;
	}

	shiftDragEnd(event, laborShift: LaborShift) {
		this.selectedLaborShift = undefined;
	}

	shiftDestinationDragDrop(event, employeeId: number, dayIndex: number) {
		let jobId: number;
		let itemDropType: string;
		if (this.dragDropLaborScheduleEntry && !this.selectedLaborShift) {
			jobId = this.dragDropLaborScheduleEntry.jobId;
			itemDropType = 'existingEntry';
		}
		if (!this.dragDropLaborScheduleEntry && this.selectedLaborShift) {
			jobId = this.selectedLaborShift.jobId;
			itemDropType = 'laborShift';
		}

		if (this.validateDropEmployeeMatchesJob(employeeId, jobId)) {
			switch (itemDropType) {
				case 'existingEntry':
					this.attachExistingEntryToSchedule(this.dragDropLaborScheduleEntry, employeeId, dayIndex);
					break;
				case 'laborShift':
					this.attachLaborShiftToSchedule(this.selectedLaborShift, employeeId, dayIndex);
					break;
			}
		} else {
			this.app.msgSvc.add({
				severity: AppMessageSeverity.RED,
				summary: '',
				detail: `${this.employeeList.find((i) => i.id === employeeId).nickName} does not have ${
					this.jobList.find((i) => i.id === this.selectedLaborShift.jobId).name
				} as an assigned job.`,
			});
		}
		this.selectedLaborShift = undefined;
	}

	validateDropEmployeeMatchesJob(employeeId: number, jobId: number) {
		const employee = this.employeeList.find((i) => i.id === employeeId);
		if (!employee) {
			this.app.msgSvc.add({ severity: AppMessageSeverity.YELLOW, summary: '', detail: `Target employee not found in list` });
			return false;
		}
		return employee.jobs.some((i) => i.id === jobId);
	}

	validateDropEmployeeFontColor(employeeId: number) {
		if (!this.selectedLaborShift && !this.dragDropLaborScheduleEntry) {
			return true;
		}
		if (!this.selectedLaborShift && this.dragDropLaborScheduleEntry) {
			return this.employeeList.find((i) => i.id === employeeId).jobs?.some((i) => i.id === this.dragDropLaborScheduleEntry.jobId);
		}
		if (this.selectedLaborShift && !this.dragDropLaborScheduleEntry) {
			return this.employeeList.find((i) => i.id === employeeId).jobs?.some((i) => i.id === this.selectedLaborShift?.jobId);
		}
	}

	validateExistingLaborScheduleEntryConflict(laborScheduleEntry: LaborScheduleEntry) {
		const existing = this.laborSchedule.LaborScheduleEntry.filter((i) => i.employeeId === laborScheduleEntry.employeeId).find(
			(i) =>
				this.app.moment(laborScheduleEntry.startDateTime).isBetween(this.app.moment(i.startDateTime), this.app.moment(i.endDateTime)) ||
				this.app.moment(laborScheduleEntry.endDateTime).isBetween(this.app.moment(i.startDateTime), this.app.moment(i.endDateTime))
		);

		if (existing) {
			this.app.confirmation.confirm({
				message: `${laborScheduleEntry.employee.nickName} is already scheduled during this time. Existing shift starts at ${this.app
					.moment(existing.startDateTime)
					.format('LT')} and ends at ${this.app.moment(existing.endDateTime).format('LT')}`,
				accept: () => {
					this.addLaborScheduleEntryToArray(laborScheduleEntry);
				},
				reject: () => {
					this.app.confirmation.close();
				},
			});
		} else {
			this.addLaborScheduleEntryToArray(laborScheduleEntry);
		}
	}
}
